import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../../components/layout/AdminLayout'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'

/**
 * Admin Users Index - Liste des utilisateurs
 */
export default function AdminUsersIndex() {
  const { users, agencies, pagination, filters } = usePage().props
  const [search, setSearch] = useState(filters.q || '')
  const [agencyFilter, setAgencyFilter] = useState(filters.agencyId || '')
  const [roleFilter, setRoleFilter] = useState(filters.role || '')

  const handleSearch = (e) => {
    e.preventDefault()
    applyFilters({})
  }

  const applyFilters = (newFilters) => {
    router.get('/admin/users', {
      q: search,
      agency_id: agencyFilter,
      role: roleFilter,
      ...newFilters
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearFilters = () => {
    setSearch('')
    setAgencyFilter('')
    setRoleFilter('')
    router.get('/admin/users', {}, { preserveState: true })
  }

  const hasActiveFilters = filters.q || filters.agencyId || filters.role

  const handleDelete = (userId) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      router.delete(`/admin/users/${userId}`)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  return (
    <AdminLayout>
      <Head title="Gestion des utilisateurs" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Utilisateurs</h1>
            <p className="mt-1 text-sm text-gray-600">
              {pagination.totalCount} utilisateur{pagination.totalCount > 1 ? 's' : ''} au total
            </p>
          </div>
          <Link href="/admin/users/new">
            <Button variant="primary">
              Nouvel utilisateur
            </Button>
          </Link>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres et recherche</h2>
          <form onSubmit={handleSearch} className="space-y-6">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un utilisateur
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  id="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Email, nom..."
                  className="flex-1 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Rechercher
                </button>
              </div>
            </div>

            {/* Filters Row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Agency Filter */}
              <div>
                <label htmlFor="agency" className="block text-sm font-medium text-gray-700 mb-2">
                  Agence
                </label>
                <select
                  id="agency"
                  value={agencyFilter}
                  onChange={(e) => {
                    setAgencyFilter(e.target.value)
                    applyFilters({ agency_id: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Toutes les agences</option>
                  {agencies.map((agency) => (
                    <option key={agency.value} value={agency.value}>
                      {agency.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle
                </label>
                <select
                  id="role"
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value)
                    applyFilters({ role: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Tous les rôles</option>
                  <option value="admin">Admin</option>
                  <option value="user">Utilisateur</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Liste */}
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Créé le
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-semibold text-sm">
                            {user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        href={`/admin/agencies/${user.agency.id}`}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {user.agency.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${user.admin
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                        }
                      `}>
                        {user.admin ? 'Admin' : 'Utilisateur'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${user.verified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                        }
                      `}>
                        {user.verified ? 'Vérifié' : 'Non vérifié'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mt-4 text-sm text-gray-500">Aucun utilisateur trouvé</p>
            </div>
          )}
        </Card>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {pagination.currentPage} sur {pagination.totalPages}
            </div>
            <div className="flex gap-2">
              {pagination.currentPage > 1 && (
                <Link href={`/admin/users?page=${pagination.currentPage - 1}`}>
                  <Button variant="outline">Précédent</Button>
                </Link>
              )}
              {pagination.currentPage < pagination.totalPages && (
                <Link href={`/admin/users?page=${pagination.currentPage + 1}`}>
                  <Button variant="outline">Suivant</Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
